import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid2,
  Dialog,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Montserrat } from "next/font/google";
import useMobile from "@/hooks/useMobile";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import PowerBiService from "@/services/main/powerBiService";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { setErrorMess } from "@/lib/features/statusSlice";
import useShowCopy from "@/hooks/useShowCopy";
import { AnimatePresence, motion } from "framer-motion";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IPowerBIType } from "@/models/powerbi";
import { useTranslations } from "next-intl";
const montserrat = Montserrat({
  subsets: ["latin"],
});

interface SaveDashboardModalProps {
  onClose?: () => void;
  open: boolean;
  onSave?: (dashboardName: string) => void;
  appliedFilters?: string[];
  reportId?: string | number;
  titleReport?: string;
  powerBi?: any | null;
  locale: string;
}
interface formData {
  bookmarkName: string;
}
const SaveDashboardModal: React.FC<SaveDashboardModalProps> = ({
  onClose,
  reportId,
  onSave,
  open,
  titleReport,
  powerBi,
  locale,
}) => {
  const isMobile = useMobile();
  const t = useTranslations("messages");
  const { savedVisuals } = useAppSelector((state) => state.powerBi);
  const [tokenUrl, setTokenUrl] = useState<string | null>(null);
  const { currentPage } = useAppSelector((state) => state.powerBi);
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const schema = useMemo(() => {
    return yup.object().shape({
      bookmarkName: yup
        .string()
        .required(t("interactive_dashboard_field_name_required")),
    });
  }, [t]);
  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
    reset,
  } = useForm<formData>({
    resolver: yupResolver(schema),
  });
  const mutationCreateBookmark = useMutation({
    mutationKey: ["create-bookmark-powerbi"],
    mutationFn: async (data: FormData) => {
      const result = await PowerBiService.createDashboard(data);
      return result?.data;
    },
    onSuccess: async (data) => {
      setTokenUrl(
        `${process.env.NEXT_PUBLIC_BASE_URL}/${locale}/interactive/${data?.token}`
      );
      queryClient.setQueryData(
        ["bookmark-dashboard", reportId],
        (oldData: any) => {
          return [data, ...oldData];
        }
      );
      reset();
    },
  });

  const handleSave = async (data: formData) => {
    const capturedBookmark = await powerBi?.bookmarksManager.capture();
    const state = capturedBookmark.state;
    if (
      !currentPage?.pageId ||
      !savedVisuals?.[currentPage?.pageId]?.slicers ||
      savedVisuals[currentPage?.pageId]?.slicers?.length === 0
    ) {
      dispatch(setErrorMess(t("interactive_dashboard_no_slicers_error")));
      return;
    }
    const form = new FormData();
    form.append("bookmarkName", data.bookmarkName);
    form.append("bookmarkState", state);
    form.append("type", IPowerBIType.BOOKMARK.toString());
    form.append(
      "bookmarkCount",
      savedVisuals[currentPage?.pageId]?.slicers?.length.toString()
    );
    if (reportId) form.append("reportId", reportId.toString());
    form.append("language", locale);
    mutationCreateBookmark.mutate(form);
  };
  const { isShow, showCopy } = useShowCopy();
  const handleCopy = (link: string | null, id: number | string): void => {
    navigator.clipboard
      .writeText(link?.toString() || "")
      .then(() => {
        showCopy({ id: id });
      })
      .catch(() => {
        dispatch(setErrorMess(t("common_copy_failed")));
      });
  };
  useEffect(() => {
    if (open) {
      reset();
      setTokenUrl(null);
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={onClose}>
      <Box
        sx={{
          overflow: "visible",
          borderRadius: "4px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          width: { xs: "90%", sm: "90%", md: "460px" },
          height: { xs: "auto", sm: "auto" },
          borderTop: "0.5px solid #D2D2D2",
          position: "relative",
          backgroundColor: "white",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ padding: 0, overflow: "visible", width: "100%" }}>
          <Paper
            elevation={0}
            sx={{ height: "100%", width: "100%" }}
            component={"form"}
            onSubmit={handleSubmit(handleSave)}
          >
            {/* Modal content */}
            <Box
              className="flex flex-col items-start"
              sx={{
                width: { xs: "100%", sm: "100%" },
                height: "100%",
              }}
            >
              {/* Title */}
              <Box
                className="grid grid-cols-12 items-center justify-between w-full min-h-[64px]"
                sx={{
                  padding: "20px 24px",
                  borderBottom: "0.5px solid #D2D2D2",
                }}
              >
                <Box className="w-full col-span-11 ">
                  <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                      fontFamily: `${montserrat.className}`,
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: "24px",
                      letterSpacing: "0%",
                      width: "100%",
                      color: "#333333",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                    translation-key="interactive_popup_save_snapshot_title"
                  >
                    {t("interactive_popup_save_snapshot_title")}
                    <Box
                      component="span"
                      sx={{
                        marginLeft: "4px",
                        fontWeight: 600,
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "0%",
                        color: "#1F61A9",
                      }}
                    >
                      &quot;{titleReport || ""}&quot;
                    </Box>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: `${isMobile ? "28px" : "32px"}`,
                    cursor: "pointer",
                    padding: "4px",
                    background: "#1C1C1C0D",
                    borderRadius: "50%",
                  }}
                  className="bg-eerie_black_5 !rounded-full z-50  col-span-1  "
                  onClick={() => onClose && onClose()}
                >
                  <CloseIcon
                    className="!text-eerie-black-40"
                    sx={{
                      fontSize: { xs: "20px", sm: "24px" },
                    }}
                  />
                </Box>
              </Box>

              {/* Applied filters section */}
              <Box sx={{ width: "100%", padding: "16px 24px" }}>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: `${montserrat.className}`,
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "24px",
                    letterSpacing: "0%",
                    color: "#000000",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                  translation-key="interactive_popup_save_snapshot_applied_filters"
                >
                  {t("interactive_popup_save_snapshot_applied_filters")}
                  {savedVisuals &&
                    currentPage?.pageId &&
                    savedVisuals[currentPage?.pageId]?.slicers?.length > 0 ? (
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {(savedVisuals as any)[(currentPage as any)?.pageId]?.slicers.flatMap((slicer: any) => slicer.value).filter(Boolean).join(", ")}
                    </Box>
                  ) : (
                    <Box translation-key="common_none" component="span" sx={{ fontWeight: 600, color: "#767676" }}>
                      {t("common_none")}
                    </Box>
                  )
                  }
                </Typography>
              </Box>

              {/* Dashboard name input */}
              {!tokenUrl ? (
                <Box sx={{ width: "100%", padding: "0 24px" }}>
                  <Typography
                    component="label"
                    htmlFor="dashboard-name"
                    sx={{
                      display: "block",
                      fontFamily: `${montserrat.className}`,
                      fontWeight: 400,
                      fontSize: { xs: "13px", sm: "14px" },
                      lineHeight: "24px",
                      letterSpacing: "0%",
                      color: "#000000",
                    }}
                    translation-key="interactive_popup_save_snapshot_dashboard_name"
                  >
                    {t("interactive_popup_save_snapshot_dashboard_name")}
                  </Typography>
                  <TextField
                    {...register("bookmarkName")}
                    id="dashboard-name"
                    translation-key="interactive_popup_save_snapshot_dashboard_name_placeholder"
                    placeholder={t(
                      "interactive_popup_save_snapshot_dashboard_name_placeholder",

                    )}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      sx: {
                        maxHeight: "40px",
                        border: "1px solid #A4A4A4",
                        borderRadius: 1,
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        // padding: '8px 16px',
                      },
                    }}
                    error={!!errors.bookmarkName}
                  />
                  {errors.bookmarkName && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{
                        fontFamily: `${montserrat.className}`,
                        fontWeight: 500,
                        fontSize: "13px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        marginTop: "4px",
                      }}
                    >
                      {errors.bookmarkName?.message}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ width: "100%", padding: "0 24px" }}>
                  <Grid2
                    size={{ sm: 9.4, xs: 8.2 }}
                    className="flex px-[8px] sm:px-[12px] py-[5px] sm:py-[8px] flex-col gap-[16px] bg-[#F4F4F4] rounded-[4px] border-solid border-[1px] border-[#E8E8E8]"
                  >
                    <Grid2
                      container
                      columnSpacing={{ xs: "9px", sm: "16px" }}
                      sx={{ width: "100%" }}
                    >
                      <Grid2
                        size={{ sm: 10.6, xs: 10 }}
                        className="flex justify-center items-center !gap-[4px]"
                      >
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            component="p"
                            sx={{
                              fontFamily: "Montserrat",
                              fontWeight: 500,
                              fontSize: { xs: "13px", sm: "16px" },
                              lineHeight: "16px",
                              color: "#767676",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tokenUrl}
                          </Typography>
                        </Box>
                      </Grid2>
                      <Grid2
                        size={{ sm: 1.4, xs: 2 }}
                        className="flex justify-center items-center relative"
                      >
                        <AnimatePresence>
                          {isShow[1] && (
                            <motion.div
                              style={{
                                minWidth: "max-content",
                                zIndex: 1000,
                              }}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeOut" }}
                              className={`absolute rounded top-[-34px] translate-x-[-50%] px-[8px] py-[4px] flex justify-center items-center !bg-black`}
                            >
                              <Typography
                                className={
                                  "!text-[13px] !text-white !font-semibold"
                                }
                                translation-key="common_copy"
                              >
                                {t("common_copy")}
                              </Typography>
                            </motion.div>
                          )}
                        </AnimatePresence>
                        <Box
                          onClick={() => handleCopy(tokenUrl, 1)}
                          className="rounded-[4px] bg-white sm:p-2 flex justify-center items-center flex-col gap-[8px]"
                        >
                          <ContentCopyIcon
                            sx={{
                              color: "black",
                              width: { xs: "20px", sm: "24px" },
                              height: { xs: "24px", sm: "24px" },
                              cursor: "pointer",
                              p: "0 4px",
                            }}
                          />
                        </Box>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Box>
              )}

              {/* Save button - aligned to right */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <Button
                  disabled={!!tokenUrl}
                  variant="contained"
                  type="submit"
                  className="report-save-snapshot-button"
                  sx={{
                    backgroundColor: "#1565c0",
                    "&:hover": {
                      backgroundColor: "#0d47a1",
                    },
                    textTransform: "none",
                    fontSize: "14px",
                    lineHeight: "24px",
                    borderRadius: 1,
                    fontWeight: 500,
                    padding: "8px 16px",
                    marginRight: "24px",
                    marginBottom: "32px",
                    marginTop: "16px",
                  }}
                >
                  {t("interactive_popup_save_snapshot_save_button")}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SaveDashboardModal;
